import { Component, Inject, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { SharedModule } from '../../shared/shared.module';
import { ICreateNgsiSubscriptionWidget, INgsiSubscriptionWidget, ModelService, PluginInjectorWidget, PluginInjectorService } from 'core-client/api';
import {
	IOiclOpenBridgeThrusterWidget, IWidgetHeaderOptions
} from '../../shared/interfaces/thruster/oicl-openbridge-thruster-widget.interface';
import * as ModuleEntityTypesConfig from '../../shared/config/module.ngsi-entity-types.constants';
import * as AzimuthThrusterConstant from '../../shared/config/azimuth-thruster.constant';
import { INgsiEntityIdAttrs } from '../../shared/interfaces/ngsi/entity.interface';
import { ISubscriptionArgs, NgsildConnectorService } from 'ngsild-connector';
import { ThrusterService } from '../services/thruster.service';


@Component({
    selector: 'lib-thruster-settings',
    imports: [SharedModule],
    templateUrl: './thruster-settings.component.html',
    styleUrl: './thruster-settings.component.scss'
})
export class ThrusterSettingsComponent implements OnInit {
	// Services
	protected readonly pluginInjectorService: PluginInjectorService = inject(PluginInjectorService);
	protected readonly modelService: ModelService = inject(ModelService);
	protected readonly translate: TranslateService = inject(TranslateService);
	readonly ngsildConnectorService: NgsildConnectorService = inject(NgsildConnectorService);
	protected readonly dialogRef: MatDialogRef<ThrusterSettingsComponent, any> =
		inject(MatDialogRef<ThrusterSettingsComponent>);
	protected readonly thrusterService: ThrusterService = inject(ThrusterService);

	// Variables
	protected openBridgeThrusterWidget: IOiclOpenBridgeThrusterWidget;
	protected readonly azimuthThrusterIdNumbers = AzimuthThrusterConstant.azimuthThrusterIdNumbers;
	protected ngsiEntityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> | undefined;
	protected entityType: string = ModuleEntityTypesConfig.NGSI_ENTITY_TYPES.thruster.entityTypes[0].DriveSystem;
	selectedWidgetHeaderOption: string = 'alwaysDisplayed';
	widgetTitle: string;
	showWidgetTitle: boolean = false;
	widgetId: string;
	selectedEntityIdPattern: string | null = '';
	selectedThrusterId: number | undefined;

	constructor(@Inject(MAT_DIALOG_DATA) protected readonly data: PluginInjectorWidget.IWidget) {
		this.openBridgeThrusterWidget = data.appPluginWidget;
		this.widgetId = data.appPluginWidget._id;
		this.widgetTitle = data.appPluginWidget.name;
		this.showWidgetTitle = data.appPluginWidget.settings.showWidgetTitle;
		const widgetHeaderOption = data.appPluginWidget.settings.widgetHeader;
		const widgetHeaderOptions = Object.keys(widgetHeaderOption) as IWidgetHeaderOptions[];
		this.selectedWidgetHeaderOption = widgetHeaderOptions.filter(
			(key: IWidgetHeaderOptions) => widgetHeaderOption[key]
		)[0];
	}

	ngOnInit(): void {
		this.ngsiEntityId = this.openBridgeThrusterWidget.settings.entityId;
		this.selectedEntityIdPattern = this.openBridgeThrusterWidget.settings.entityId?.idPattern ?? null;
		this.selectedThrusterId = this.openBridgeThrusterWidget.settings?.thrusterId;
	}

	selectHeaderOption($event: MatSelectChange): void {
		const headerOptions: Record<string, { alwaysDisplayed: boolean; onHoverDisplayed: boolean; alwaysOff: boolean; transparent: boolean }> = {
			alwaysDisplayed: { alwaysDisplayed: true, onHoverDisplayed: false, alwaysOff: false, transparent: false },
			onHoverDisplayed: { alwaysDisplayed: false, onHoverDisplayed: true, alwaysOff: false, transparent: false },
			alwaysOff: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: true, transparent: false },
			transparent: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: false, transparent: true }
		};

		if ($event.value in headerOptions) {
			this.openBridgeThrusterWidget.settings.widgetHeader = headerOptions[$event.value];
		}
	}

	getText(key: unknown): string {
		const translations: Record<string, string> = {
			alwaysDisplayed: 'OICL_OPENBRIDGE.THRUSTER.WIDGET.WIDGET_HEADER.ALWAYS_DISPLAYED',
			onHoverDisplayed: 'OICL_OPENBRIDGE.THRUSTER.WIDGET.WIDGET_HEADER.ON_HOVER_DISPLAYED',
			alwaysOff: 'OICL_OPENBRIDGE.THRUSTER.WIDGET.WIDGET_HEADER.ALWAYS_OFF',
			transparent: 'OICL_OPENBRIDGE.THRUSTER.WIDGET.WIDGET_HEADER.TRANSPARENT'
		};

		return this.translate.instant(translations[key as string] || 'unknown');
	}

	async onSubmit(): Promise<void> {
		if (!this.openBridgeThrusterWidget) {
			return;
		}

		// update variables
		this.openBridgeThrusterWidget.name = this.widgetTitle;
		this.openBridgeThrusterWidget.settings.showWidgetTitle = this.showWidgetTitle;

		if (typeof this.selectedEntityIdPattern === 'string') {
			this.openBridgeThrusterWidget.settings.entityId = {
				type: this.entityType,
				owner: 'CAPTN',
				idPattern: this.selectedEntityIdPattern
			};

			// Empty MMSI
			if (!this.ngsiEntityId?.idPattern) {
				this.ngsiEntityId = this.openBridgeThrusterWidget.settings.entityId;
			}
		}

		this.openBridgeThrusterWidget.settings.thrusterId = this.selectedThrusterId;

		// save changes to database
		await this.modelService._appPluginWidget.upsertOne(this.openBridgeThrusterWidget);

		// Manage NGSI-LD Subscriptions
		if (typeof this.selectedEntityIdPattern === 'string') {
			await this.manageNgsiSubscriptions();
		}

		// Update widget name
		// Directly data-binding changes
		const widget = this.pluginInjectorService.widgetService.getWidgetItem(this.openBridgeThrusterWidget._id);
		if (widget) {
			widget.name = this.openBridgeThrusterWidget.name;
		}

		if (this.selectedThrusterId) {
			this.thrusterService.setThrusterId(this.widgetId, this.selectedThrusterId);
		}

		if (this.ngsiEntityId?.idPattern) {
			this.thrusterService.setThrusterEntity(this.widgetId, this.ngsiEntityId);
		}

		this.dialogRef.close();
	}

	// Subscription management
	async manageNgsiSubscriptions(): Promise<void> {
		// List of all tracked NGSI widget subscriptions
		const ngsiSubscriptionWidgets=
			await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);

		let currentEntityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined;
		if (this.ngsiEntityId?.idPattern) {
			currentEntityId = { ...this.ngsiEntityId };
			currentEntityId.idPattern = this.selectedEntityIdPattern;
		}

		const ngsiSubscriptionWidget =
			ngsiSubscriptionWidgets.find((ngsiSubscriptionWidget: INgsiSubscriptionWidget) =>
				!ngsiSubscriptionWidget.geoQSub && ngsiSubscriptionWidget.app_plugin_widget === this.widgetId &&
				ngsiSubscriptionWidget.entityId === this.getWidgetEntityId(this.ngsiEntityId)
			);

		const existingSubToEntityId =
			ngsiSubscriptionWidgets.find((ngsiSubscriptionWidget: INgsiSubscriptionWidget) =>
				!ngsiSubscriptionWidget.geoQSub &&
				ngsiSubscriptionWidget.entityId === this.getWidgetEntityId(currentEntityId)
			);

		// Subscription for entityId already exist for widget
		if (this.ngsiEntityId?.idPattern === currentEntityId?.idPattern && existingSubToEntityId != undefined && ngsiSubscriptionWidget != undefined) {
			return;
		}

		// subscription for entityId already exist but is still missing for widget
		if (this.ngsiEntityId?.idPattern === currentEntityId?.idPattern && existingSubToEntityId != undefined && ngsiSubscriptionWidget == undefined) {
			const ngsiSubscriptionWidget = {
				app_plugin_widget: this.widgetId,
				subscriptionId: existingSubToEntityId.subscriptionId,
				type: currentEntityId?.type,
				entityId: this.getWidgetEntityId(currentEntityId),
				geoQSub: false,
				startPosition: null,
				lastKnowPosition: null,
				lastUpdated: new Date()
			} as unknown as ICreateNgsiSubscriptionWidget

			await this.modelService._ngsiSubscriptionWidget.upsertOne(ngsiSubscriptionWidget);
			return;
		}

		// Subscription don`t exist
		if (!existingSubToEntityId && currentEntityId) {
			// create subscription for widgetId
			await this.addNgsiSubscription(currentEntityId);
		} else if (existingSubToEntityId && ngsiSubscriptionWidget && this.ngsiEntityId?.idPattern && currentEntityId?.idPattern) {
			// update old subscription
			await this.updateNgsiSubscription(
				ngsiSubscriptionWidget,
				existingSubToEntityId,
				this.ngsiEntityId,
				currentEntityId
			);
		}
	}

	protected getTranslation(viewValue: string): string {
		return this.translate.instant(`OICL_OPENBRIDGE.THRUSTER.WIDGET.${viewValue}`);
	}

	private async addNgsiSubscription(currentEntityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix">): Promise<void> {
		const uuid = await this.ngsildConnectorService.uuidFromSettings();

		let currentSubsArgs = {
			uuid: uuid,
			appPluginWidgetId: this.widgetId
		} as ISubscriptionArgs;

		if (currentEntityId?.idPattern && currentEntityId?.type && currentEntityId?.owner) {
			currentSubsArgs.params = {
				type: currentEntityId.type,
				idPattern: currentEntityId.idPattern,
				owner: currentEntityId.owner
			};

			this.ngsildConnectorService.requestNotificationForEntity(currentSubsArgs);
		}
	}

	private async updateNgsiSubscription(
		ngsiSubscriptionWidget: INgsiSubscriptionWidget,
		existingSubToEntityId: INgsiSubscriptionWidget,
		previousEntityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix">,
		currentEntityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix">
	): Promise<void> {
		const uuid = await this.ngsildConnectorService.uuidFromSettings();
		const appPluginWidgetId = this.widgetId;

		let currentSubsArgs = {
			uuid: uuid,
			appPluginWidgetId: appPluginWidgetId,				   // appPluginWidgetId referenz
			subscriptionId: existingSubToEntityId.subscriptionId,  // new subscriptionId referenz
			ngsiSubscriptionWidgetId: ngsiSubscriptionWidget._id,  // update ngsiSubscriptionWidgetId referenz
		} as ISubscriptionArgs;

		if (currentEntityId?.idPattern && currentEntityId?.type && currentEntityId?.owner) {
			currentSubsArgs.params = {
				type: currentEntityId.type,
				idPattern: currentEntityId.idPattern,
				owner: currentEntityId.owner
			};
		}

		let previousSubsArgs = {
			uuid: uuid,
			appPluginWidgetId: appPluginWidgetId,
			ngsiSubscriptionWidgetId: ngsiSubscriptionWidget._id,
		} as ISubscriptionArgs;

		if (previousEntityId?.idPattern && previousEntityId?.type && previousEntityId?.owner) {
			previousSubsArgs.params = {
				type: previousEntityId.type,
				idPattern: previousEntityId.idPattern,
				owner: previousEntityId.owner
			};
		}

		if (!Array.isArray(currentSubsArgs.params) && currentSubsArgs?.params?.idPattern &&
			!Array.isArray(previousSubsArgs.params) && previousSubsArgs?.params?.idPattern
		) {
			this.ngsildConnectorService.requestNotificationForEntity(currentSubsArgs, previousSubsArgs);
		}
	}

	private getWidgetEntityId(entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): string | undefined {
		if (!entityId?.idPattern) {
			return undefined;
		}

		return entityId?.owner ?
			`${ModuleEntityTypesConfig.NGSI_ENTITY_ID_PREFIX}${entityId?.type}:${entityId?.owner}:${entityId?.idPattern}` :
			`${ModuleEntityTypesConfig.NGSI_ENTITY_ID_PREFIX}${entityId?.type}:${entityId?.idPattern}`;
	}
}
